import { spotlightTheme } from './themes'

/**
 * @title spotlights
 * @description This file contains the spotlights for the PennyLane website.
 *
 * Directory:
 * Some helpful links for configuring spotlights:
 * ----------------------------------------------
 * @link Readme file://./README.md}
 * Documentation on how to configure spotlights.
 * @link Themes file://./themes.ts}
 * Themes are used to style the spotlights.
 * @link Templates file://./templates.md}
 * Templates are copy and paste-able code snippets that can be used to create new spotlights.
 */

export default {
  /**
   * @title home_page
   * @description
   * This spotlight is located on the home page of the website.
   * @link https://pennylane.ai/
   */
  home_page: {
    hidden: false,
    announcements: [
        {
        title: 'QUANTUM COMPILATION',
        description:
          "Compiling large quantum algorithms for fault-tolerant hardware is crucial—but knowing which techniques to use can be daunting. Our modern Quantum Compilation overview provides clear explanations and guidance to help you get started.",
        image:
          'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/Spotlight_CompilationLaunch.png',
        image_right_aligned: false,
        alt: '',
        links: [
          {
            link_text: 'Explore Quantum Compilation techniques',
            link: '/compilation',
          },
          // {
          //   link_text: 'Visit the Quantum Compilation wiki',
          //   link: '/compilation',
          // },          
        ],
        colours: spotlightTheme.theme_2,
        },
        {
        title: 'NEW RELEASE',
        description:
          "PennyLane v0.42 and Catalyst v0.12 are out, featuring improved Select decomposition, a new QSVT/QSP angle solver, enhanced Qualtran integration, efficient Clifford+T decomposition, Pauli product measurement compilation, and more!",
        image:
          'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/pennylane-spotlight-release-v0-42.png',
        image_right_aligned: true,
        alt: '',
        links: [
          {
            link_text: 'Read the blog post!',
            link: '/blog/2025/07/pennylane-release-0.42/',
          },
          // {
          //   link_text: 'Visit the PennyLane documentation',
          //   link: 'https://docs.pennylane.ai/en/stable/',
          // },          
        ],
        colours: spotlightTheme.theme_1,
        },
    ],
  },

  /**
   * @title education_page
   * @description This located on the education page of the website.
   * @link https://pennylane.ai/education/
   */
  education_page: {
    hidden: false,
    announcements: [      
      {
        title: 'PENNYLANE FUNDAMENTALS CODEBOOK MODULE',
        description:
          "Accelerate your quantum research breakthroughs with PennyLane! Quickly learn fundamental functions and practical applications of theoretical concepts in this brand-new module.",
        image:
          'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/Spotlight_CodebookLaucnh_2024-04-30.png',
        image_right_aligned: true,
        alt: '',
        links: [
          {
            link_text: 'Learn more',
            link: '/codebook/pennylane-fundamentals',
          },
        ],
        colours: spotlightTheme.theme_1,
      },
      {
        title: 'EASILY NAVIGATE THE PENNYLANE CODEBOOK',
        description:
          "Delve into the advanced topic you've been searching for, or explore some of the most popular learning paths taught by over 130 universities across 35 countries worldwide!",
        image:
          'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/Spotlight_PLCodebookMap.png',
        image_right_aligned: false,
        alt: '',
        links: [
          {
            link_text: 'Start here!',
            link: '/codebook/learning-paths',
          },
        ],
        colours: spotlightTheme.theme_2,
      },
    ],
  },

  /**
   * @title learn_page
   * @description This spotlight is located on the learn page of the website.
   * @link https://pennylane.ai/qml/
   */
  learn_page: {
    hidden: false,
    announcements: [
      {
        title: 'EASILY NAVIGATE THE PENNYLANE CODEBOOK',
        description:
          "Delve into the advanced topic you've been searching for, or explore some of the most popular learning paths taught by over 110 universities across 34 countries worldwide!",
        image:
          'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/Spotlight_PLCodebookMap.png',
        image_right_aligned: true,
        alt: '',
        links: [
          {
            link_text: 'Start here!',
            link: '/codebook/learning-paths',
          },
        ],
        colours: spotlightTheme.theme_1,
      },
      {
        title: 'FRENCH CODEBOOK MODULES NOW AVAILABLE',
        description: "As a proudly Canadian quantum software framework, we're excited to now offer five modules of the PennyLane Codebook in French.",
        image: 'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/Spotlight_CodebookLaucnh_2024-04-30.png',
        image_right_aligned: false,
        alt: '',
        links: [
          {
            link_text: 'Check them out!',
            link: '/codebook/01-introduction-to-quantum-computing/01-all-about-qubits/fr/',
          },
        ],
        colours: spotlightTheme.theme_2,
      },
    ],
  },

  /**
   * @title performance_page
   * @description This spotlight is located on the performance page of the website.
   * https://pennylane.ai/performance/
   */
  performance_page: {
    hidden: false,
    announcements: [
      {
        title: 'LIGHTNING-GPU MEETS CATALYST',
        description:
          'PennyLane’s Lightning-GPU simulator device is now integrated with Catalyst, enabling high-performance quantum simulations on NVIDIA GPUs.',
        image:
          'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/Spotlight_Lightning_GPU_Catalyst.png',
        image_right_aligned: true,
        alt: '',
        links: [
          {
            link_text: 'Read the demo',
            link: '/qml/demos/how_to_catalyst_lightning_gpu',
          },
          {
            link_text: 'Visit the Lightning-GPU documentation',
            link: 'https://docs.pennylane.ai/projects/lightning/en/stable/lightning_gpu/device.html',
          },
        ],
        colours: spotlightTheme.theme_1,
      },
      {
        title: 'PERFORMANCE',
        description:
          'PennyLane is faster than ever. Learn how to implement large-scale simulations on supercomputers and GPUs with our latest paper and blog post.',
        image:
          'https://assets.cloud.pennylane.ai/datasets/spotlight/Spotlight_HPC4UandMe.png',
        image_right_aligned: false,
        alt: '',
        links: [
          {
            link_text: 'Read the paper',
            link: 'https://arxiv.org/abs/2403.02512',
          },
          {
            link_text: 'Check out the blog',
            link: 'https://pennylane.ai/blog/2024/03/hpc-4-u-and-me/',
          },
        ],
        colours: spotlightTheme.theme_2,
      },
    ],
  },

  /**
   * @title install_page
   * @description This spotlight is located on the install page of the website.
   * @link https://pennylane.ai/install/
   */
  install_page: {
    hidden: false,
    announcements: [
      // {
      //   title: 'SURVEY CLOSES OCTOBER 3, 2025',
      //   description:
      //     "The Unitary Foundation 2025 Quantum Open Source Software Survey is now open. Share your voice to help build a better quantum computing ecosystem! This should take approximately 10 minutes.",
      //   image:
      //     'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/pennylane-spotlight-unitary-foundation-thumbnail.png',
      //   image_right_aligned: false,
      //   alt: '',
      //   links: [
      //     {
      //       link_text: 'Take the survey!',
      //       link: 'https://unitary.foundation/posts/2025_qoss_survey/',
      //     },
      //     {
      //       link_text: 'Read the blog post',
      //       link: '/blog/2025/09/unitary-found-quantum-open-source-software-survey-2025',
      //     },       
      //   ],
      //   colours: spotlightTheme.theme_2,
      // },
    ],
  },

  /**
   * @title get_involved_page
   * @description This spotlight is located on the get involved page of the website.
   * @link https://pennylane.ai/get-involved/
   */
  get_involved_page: {
    hidden: false,
    announcements: [
      // {
      //   title: 'SURVEY CLOSES OCTOBER 3, 2025',
      //   description:
      //     "The Unitary Foundation 2025 Quantum Open Source Software Survey is now open. Share your voice to help build a better quantum computing ecosystem! This should take approximately 10 minutes.",
      //   image:
      //     'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/pennylane-spotlight-unitary-foundation-thumbnail.png',
      //   image_right_aligned: false,
      //   alt: '',
      //   links: [
      //     {
      //       link_text: 'Take the survey!',
      //       link: 'https://unitary.foundation/posts/2025_qoss_survey/',
      //     },
      //     {
      //       link_text: 'Read the blog post',
      //       link: '/blog/2025/09/unitary-found-quantum-open-source-software-survey-2025',
      //     },       
      //   ],
      //   colours: spotlightTheme.theme_2,
      // },
    ],
  },

  /**
   * @title features_page
   * @description This spotlight is located on the features page of the website.
   * @link https://pennylane.ai/features/
   */
  features_page: {
    hidden: false,
    announcements: [
      {
        title: 'NEW RELEASE',
          description:
            "PennyLane v0.42 and Catalyst v0.12 are out, featuring improved Select decomposition, a new QSVT/QSP angle solver, enhanced Qualtran integration, efficient Clifford+T decomposition, Pauli product measurement compilation, and more!",
        image:
          'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/pennylane-spotlight-release-v0-42.png',
        image_right_aligned: true,
        alt: '',
        links: [
          {
            link_text: 'Read the blog post!',
            link: '/blog/2025/07/pennylane-release-0.42/',
          },
          {
            link_text: 'Visit the PennyLane documentation',
            link: 'https://docs.pennylane.ai/en/stable/',
          },      
        ],
        colours: spotlightTheme.theme_1,
      },
    ],
  },

  /**
   * @title codebook_page
   * @description This spotlight is located on the codebook page of the website.
   * @link https://pennylane.ai/codebook/
   */
  codebook_page: {
    hidden: false,
    announcements: [  
      {
        title: 'PENNYLANE FUNDAMENTALS CODEBOOK MODULE',
        description:
          "Accelerate your quantum research breakthroughs with PennyLane! Quickly learn fundamental functions and practical applications of theoretical concepts in this brand-new module.",
        image:
          'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/Spotlight_CodebookLaucnh_2024-04-30.png',
        image_right_aligned: true,
        alt: '',
        links: [
          {
            link_text: 'Learn more',
            link: '/codebook/pennylane-fundamentals',
          },
        ],
        colours: spotlightTheme.theme_2,
      },
      {
        title: 'EASILY NAVIGATE THE PENNYLANE CODEBOOK',
        description:
          "Delve into the advanced topic you've been searching for, or explore some of the most popular learning paths taught by over 110 universities across 34 countries worldwide!",
        image:
          'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/Spotlight_PLCodebookMap.png',
        image_right_aligned: false,
        alt: '',
        links: [
          {
            link_text: 'Start here!',
            link: '/codebook/learning-paths',
          },
        ],
        colours: spotlightTheme.theme_1,
      },
    ],
  },

  /**
   * @title challenges_page
   * @description This spotlight is located on the challenges page of the website.
   * @link https://pennylane.ai/challenges/
   */
  challenges_page: {
    hidden: false,
    announcements: [
      // {
      //   title: 'SURVEY CLOSES OCTOBER 31, 2024',
      //   description:
      //     'The Unitary Foundation 2024 Quantum Open Source Software Survey is here! Share your voice to help build a better quantum computing ecosystem.',
      //   image:
      //     'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/Spotlight_UnitaryFundSurvey.png',
      //   image_right_aligned: false,
      //   alt: '',
      //   links: [
      //     {
      //       link_text: 'Take the survey',
      //       link: 'https://www.surveymonkey.com/r/qosssurvey24',
      //     },
      //   ],
      //   colours: spotlightTheme.theme_1,
      // },
    ],
  },
  /**
   * @title research_page
   * @description This spotlight is located on the research page of the website.
   * @link https://pennylane.ai/research/
   */
  research_page: {
    hidden: false,
    announcements: [
      {
        title: 'QUANTUM COMPILATION',
        description:
          "Compiling large quantum algorithms for fault-tolerant hardware is crucial—but knowing which techniques to use can be daunting. Our modern Quantum Compilation overview provides clear explanations and guidance to help you get started.",
        image:
          'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/Spotlight_CompilationLaunch.png',
        image_right_aligned: true,
        alt: '',
        links: [
          {
            link_text: 'Explore Quantum Compilation techniques',
            link: '/compilation',
          },
          // {
          //   link_text: 'Visit the Quantum Compilation wiki',
          //   link: '/compilation',
          // },          
        ],
        colours: spotlightTheme.theme_1,
      },
      {
        title: 'HAMILTONIAN SIMULATION',
        description:
          "Hamiltonian simulation is one the most promising uses of quantum computing, with high potential for industrial applications. We created this guide to help you keep up with the constant developments.",
        image:
          'https://assets.cloud.pennylane.ai/pennylane_website/spotlights/pennylane-spotlight-quantum-topics.png',
        image_right_aligned: false,
        alt: '',
        links: [
          {
            link_text: 'Check out the guide',
            link: '/topics/hamiltonian-simulation',
          },
          // {
          //   link_text: 'Check out the documentation',
          //   link: 'https://docs.pennylane.ai/en/stable/',
          // },
        ],
        colours: spotlightTheme.theme_2,
      },
    ],
  },
}
